import { WGSLNodeBuilder } from 'three/webgpu';

/**
 * ── ⭐ 1,952 VERTEX SHADERS THAT DIFFER ONLY BY AN IDENTIFIER ────────────────────────────────────
 *
 * `mars-hangar-01` reports **1,953 programs**, and they split **1,952 vertex / 16 fragment**. A
 * live diff of the sources found them identical except for one token:
 *
 *     struct NodeBuffer_10401Struct { … }      // one InstancedMesh
 *     struct NodeBuffer_10558Struct { … }      // the next one, byte-identical otherwise
 *
 * Only **five** distinct array sizes and 43 distinct source lengths exist across the whole set, so
 * the overwhelming majority of those compiles are the same work done again under a different name.
 *
 * The name comes from three, here:
 *   · `Instance.js:45` builds the instance-matrix uniform with `buffer( instanceMatrix.array, … )`,
 *     a fresh node per `InstancedMesh`, and never names it.
 *   · `UniformNode.generate:176` passes `this.name || builder.context.nodeName` down.
 *   · `WGSLNodeBuilder:1326` then falls back to `'NodeBuffer_' + uniformNode.id` — a GLOBAL node id.
 *
 * ⛔ **THERE IS NO APPLICATION-LEVEL ROUTE.** The node is constructed inside three, per mesh, and
 * neither the `InstancedMesh` nor its `instanceMatrix` reaches it. The alternatives were a patch to
 * `node_modules` (invisible to a fresh clone, lost on install) or this: `WGSLNodeBuilder` is a
 * public export of `three/webgpu` (`Three.WebGPU.js:37`), so the one function that computes the
 * fallback name is wrapped, in our own source, where a reader can see it.
 *
 * ── WHAT THIS DOES AND DOES NOT BUY, BECAUSE TWO INVESTIGATIONS DISAGREED ───────────────────────
 *
 * It does **not** reduce the number of node BUILDS. `nodeBuilderCache` is keyed by
 * `initialCacheKey`, which contains `object.uuid`, so each mesh still builds its own graph and the
 * measured ~7.6 s of `NodeBuilder.build()` on a cold camera sweep remains. **A run where
 * `nodeBuilderCache` still reads ~1,953 afterwards is the EXPECTED result, not a failed fix.**
 *
 * What it collapses is `Pipelines.programs.vertex`, which is interned by SOURCE STRING: identical
 * sources dedupe, `createProgram` is skipped, and `_getRenderCacheKey` collapses with it because
 * `backend.getRenderCacheKey` carries no uuid. The open question — and the reason this is landed
 * with a falsifiable prediction rather than a claim — is how much of the cold sweep's **22.8 s of
 * off-thread stall** (107 discrete 50 ms–1 s gaps with no JS on the stack, against 0 such gaps
 * warm) is GPU-process shader compilation that this removes. One investigation predicts nearly all
 * of it; the other predicts ~0.6 %. The measurement decides, and both predictions are recorded here
 * so the answer cannot be written afterwards to match whatever happened.
 *
 * ⭐ **IT IS SAFE ALONE, AND THE NEIGHBOURING CHANGE IS NOT.** This alters the WGSL *text*, never a
 * cache *key*: `initialCacheKey` still carries `object.uuid`, so every mesh keeps its own
 * `nodeBuilderState` and its own bindings, and only the `ProgrammableStage` and the pipeline are
 * shared. Removing that uuid — three's own TODO at `RenderObject.js:833-839` — would be a different
 * and dangerous change: `NodeUniformBuffer`'s constructor captures `nodeUniform.value`, i.e. the
 * FIRST mesh's `Float32Array`, so two meshes sharing one `nodeBuilderState` would both draw the
 * first mesh's matrices. A silently wrong picture. Naming does nothing about that, and must not be
 * mistaken for a step toward it.
 */

let installed = false;

/**
 * Give every unnamed WGSL buffer a name that depends on its position within its own build rather
 * than on a global counter, so two builds that are otherwise identical produce identical source.
 *
 * Idempotent, and a no-op on the WebGL2 fallback, which uses a different builder entirely.
 */
export function installSharedBufferNames(): void {
  if (installed) return;
  installed = true;

  const proto = WGSLNodeBuilder.prototype as unknown as {
    getUniformFromNode: (node: object, type: string, shaderStage: string, name?: string | null) => unknown;
  };
  const original = proto.getUniformFromNode;

  proto.getUniformFromNode = function patched(
    this: { _fwgBufferNames?: Map<object, string> },
    node: object,
    type: string,
    shaderStage: string,
    name: string | null = null,
  ): unknown {
    /*
     * Only the unnamed buffer case. A node that already carries a name is left exactly alone — this
     * exists to replace a global id with something stable, never to rename anything three or we
     * deliberately named.
     */
    if (name === null && (type === 'buffer' || type === 'storageBuffer')) {
      /*
       * ⚠️ A PER-BUILDER MEMO KEYED BY NODE, NOT A COUNTER. The same node is asked for once in the
       * vertex build and again in the fragment build, and the two names MUST agree or the struct is
       * declared under one name and read under another. Keying by node also keeps two distinct
       * buffers inside one build distinct, which a shared counter would not guarantee across
       * builds.
       *
       * The index is the memo's size, i.e. the order this build first met the node — so a build
       * with one instance buffer always names it `NodeBuffer_0`, whatever the node's global id
       * happens to be, which is the entire point.
       */
      const memo = (this._fwgBufferNames ??= new Map<object, string>());
      let shared = memo.get(node);
      if (shared === undefined) {
        shared = `NodeBuffer_${memo.size}`;
        memo.set(node, shared);
      }
      name = shared;
    }
    return original.call(this, node, type, shaderStage, name);
  } as typeof original;
}
